import { CoreInput } from '@/components/Form/InputAntForm';
import { AccountsEntity } from '@/domain/entities/account';
import { Avatar, Button, Card, DatePicker, Modal, Switch, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { RolesSelect } from '../../components/RolesSelect';
import useViewModel, { EmailTypeEnum } from '../../viewmodels/form';
import { CoreSwitch } from '@/components/Form/Switch';
import CoreSelect from '@/components/Form/SelectAntForm';
import { ExclamationCircleFilled, UserOutlined } from '@ant-design/icons';

interface Props {
  detailAccount: AccountsEntity;
  openForm: boolean;
  showId: string | null;
}

export const AccountForm = (props: Props) => {
  const { openForm, showId } = props;
  const {
    methodForm,
    onSubmit,
    defaultValue,
    onResetMfa,
    onSwitchRequiredMfa,
    detailAccount,
    passwordChangeForm,
    onChangePassword,
    isChangePasswordModalOpen,
    handleOpenChangePasswordModal,
    handleCloseChangePasswordModal,
    emailChangeForm,
    onChangeEmail,
    isChangeEmailModalOpen,
    handleOpenChangeEmailModal,
    handleCloseChangeEmailModal,
  } = useViewModel(props);

  const {
    control,
    reset,
    setValue,
    formState: { isSubmitting, isDirty },
  } = methodForm;

  const {
    control: passwordControl,
    formState: { isSubmitting: isPasswordSubmitting, isDirty: isPasswordDirty },
    watch: passwordWatch,
    reset: passwordReset,
  } = passwordChangeForm;

  const {
    control: emailControl,
    formState: { isSubmitting: isEmailSubmitting, isDirty: isEmailDirty },
    watch: emailWatch,
    reset: emailReset,
  } = emailChangeForm;

  const newPassword = passwordWatch('newPassword');
  const confirmPassword = passwordWatch('confirmPassword');
  const newEmail = emailWatch('newEmail');

  const username = useWatch({ control, name: 'username' });
  const email = useWatch({ control, name: 'email' });
  const emailType = useWatch({ control, name: 'emailType' });
  const dob = useWatch({ control, name: 'dob' });

  const handleResetMfa = () => {
    Modal.confirm({
      title: 'Reset MFA',
      content: 'Are you sure you want to reset MFA for this account? This action cannot be undone.',
      okText: 'Yes, Reset MFA',
      okButtonProps: { danger: true },
      cancelText: 'No, Cancel',
      onOk: onResetMfa,
    });
  };

  const handleSwitchRequiredMfa = (checked: boolean) => {
    if (!checked) {
      Modal.confirm({
        title: 'Disable MFA Requirement',
        icon: <ExclamationCircleFilled />,
        content:
          'Are you sure you want to disable MFA requirement for this account? This may reduce account security.',
        okText: 'Yes, Disable MFA',
        okButtonProps: {
          danger: true,
          type: 'default',
        },
        cancelText: 'No, Cancel',
        onOk: () => onSwitchRequiredMfa(false),
      });
    } else {
      onSwitchRequiredMfa(true);
    }
  };

  const handleClosePasswordModal = () => {
    passwordReset();
    handleCloseChangePasswordModal();
  };

  const handleCloseEmailModal = () => {
    emailReset();
    handleCloseChangeEmailModal();
  };

  useEffect(() => {
    if (emailType === EmailTypeEnum.AUTO_GENERATED && !showId) {
      setValue('email', '');
    }
  }, [emailType, setValue, showId]);

  useEffect(() => {
    if (!showId) {
      reset(defaultValue);
    }
  }, [openForm]);

  const cardBodyStyle = { padding: '12px' };
  const fieldLabelClass = 'block font-medium text-gray-700 text-sm';
  const compactPanelClass =
    'bg-gray-50 rounded-lg px-3 py-1.5 flex items-center justify-between gap-2';
  const cardClassName = 'shadow-none !mb-0';
  const sectionTitleClass = '!mb-0 !mt-0 !text-base text-gray-700 leading-tight';
  const sectionSubtitleClass = '!mb-2 !mt-0.5 !text-sm leading-snug';

  return (
    <div className={showId ? 'h-full flex flex-col' : undefined}>
      <form
        onSubmit={onSubmit}
        className={showId ? 'flex-1 overflow-y-auto' : undefined}
      >
        <div className={showId ? 'px-4 py-3 space-y-2' : 'px-0 pt-0 pb-2 space-y-2'}>
          {showId && (
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-1'>
              <div className='flex items-center gap-3 min-h-[32px]'>
                <Avatar size={40} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                <div className='flex flex-col justify-center'>
                  <Typography.Text className='text-sm font-medium text-gray-700 leading-5'>
                    {username || '-'}
                  </Typography.Text>
                  <Typography.Text type='secondary' className='text-xs leading-4'>
                    {email || '-'}
                  </Typography.Text>
                </div>
              </div>
              <div className='flex flex-wrap items-center gap-2 justify-end'>
                <Button
                  danger
                  type='default'
                  htmlType='button'
                  onClick={handleResetMfa}
                  loading={isSubmitting}
                  size='middle'
                >
                  Reset MFA
                </Button>
                <Button
                  type='default'
                  htmlType='button'
                  onClick={handleOpenChangePasswordModal}
                  loading={isSubmitting}
                  size='middle'
                >
                  Change Password
                </Button>
                <Button
                  type='default'
                  htmlType='button'
                  onClick={handleOpenChangeEmailModal}
                  loading={isSubmitting}
                  size='middle'
                >
                  Change Email
                </Button>
              </div>
            </div>
          )}

          {/* Section 2 — Account Information */}
          <Card bordered={false} className={cardClassName} styles={{ body: cardBodyStyle }}>
            <Typography.Title level={5} className={sectionTitleClass}>
              Account Information
            </Typography.Title>
            <Typography.Paragraph type='secondary' className={sectionSubtitleClass}>
              Basic login credentials for this user.
            </Typography.Paragraph>
            <div className='space-y-2'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>
                    Username <span className='text-red-500'>*</span>
                  </Typography.Text>
                  <CoreInput control={control} name='username' className='w-full' />
                </div>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>
                    Email <span className='text-red-500'>*</span>
                  </Typography.Text>
                  <CoreInput
                    control={control}
                    name='email'
                    className='w-full'
                    disabled={emailType === EmailTypeEnum.AUTO_GENERATED || !!showId}
                  />
                </div>
              </div>
              <div className={`${compactPanelClass} flex-col sm:flex-row sm:items-center`}>
                <div className='flex-1 min-w-0'>
                  <Typography.Text className='text-sm font-medium text-gray-700'>
                    Auto Create Email
                  </Typography.Text>
                  <Typography.Text type='secondary' className='text-xs block leading-snug'>
                    Automatically generate an email address from the username.
                  </Typography.Text>
                </div>
                <div className='shrink-0 self-center sm:self-auto'>
                  <CoreSwitch
                    control={control}
                    name='emailType'
                    checkedValue={EmailTypeEnum.AUTO_GENERATED}
                    unCheckedValue={EmailTypeEnum.USER_PROVIDED}
                    disabled={!!showId}
                    defaultChecked={showId ? false : undefined}
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Section 3 — Personal Details */}
          <Card bordered={false} className={cardClassName} styles={{ body: cardBodyStyle }}>
            <Typography.Title level={5} className={sectionTitleClass}>
              Personal Details
            </Typography.Title>
            <Typography.Paragraph type='secondary' className={sectionSubtitleClass}>
              Contact information and personal profile.
            </Typography.Paragraph>
            <div className='space-y-2'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>Phone Number</Typography.Text>
                  <CoreInput control={control} name='phoneNo' className='w-full' />
                </div>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>Gender</Typography.Text>
                  <CoreSelect
                    control={control}
                    name='gender'
                    optionsProps={[
                      {
                        value: 'male',
                        label: 'Male',
                      },
                      {
                        value: 'female',
                        label: 'Female',
                      },
                    ]}
                    displayField='label'
                    valueField='value'
                    className='w-full'
                  />
                </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-2'>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>Family Name</Typography.Text>
                  <CoreInput control={control} name='lastName' className='w-full' />
                </div>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>Middle Name</Typography.Text>
                  <CoreInput control={control} name='middleName' className='w-full' />
                </div>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>Given Name</Typography.Text>
                  <CoreInput control={control} name='firstName' className='w-full' />
                </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>Birth Date</Typography.Text>
                  <DatePicker
                    picker='date'
                    allowClear={false}
                    className='w-full'
                    style={{ width: '100%' }}
                    value={dob ? dayjs(dob) : undefined}
                    onChange={(value) => {
                      setValue('dob', value?.toISOString() ?? '', { shouldDirty: true });
                    }}
                  />
                </div>
                <div className='space-y-1'>
                  <Typography.Text className={fieldLabelClass}>Address</Typography.Text>
                  <CoreInput control={control} name='address' className='w-full' />
                </div>
              </div>
            </div>
          </Card>

          {/* Section 4 — Account Settings */}
          <Card bordered={false} className={cardClassName} styles={{ body: cardBodyStyle }}>
            <Typography.Title level={5} className={sectionTitleClass}>
              Account Settings
            </Typography.Title>
            <Typography.Paragraph type='secondary' className={sectionSubtitleClass}>
              Access, security and role assignments.
            </Typography.Paragraph>
            <div className='space-y-2'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                <div className={compactPanelClass}>
                  <div className='flex-1 min-w-0'>
                    <Typography.Text className='text-sm font-medium text-gray-700'>Status</Typography.Text>
                  </div>
                  <div className='shrink-0'>
                    <CoreSwitch control={control} name='isActive' formatValue={true} />
                  </div>
                </div>
                {showId && (
                  <div className={compactPanelClass}>
                    <div className='flex-1 min-w-0'>
                      <Typography.Text className='text-sm font-medium text-gray-700'>
                        MFA required
                      </Typography.Text>
                      <Typography.Text type='secondary' className='text-xs block leading-snug'>
                        Require two-factor authentication at sign in.
                      </Typography.Text>
                    </div>
                    <div className='shrink-0'>
                      <Switch
                        onChange={handleSwitchRequiredMfa}
                        checked={detailAccount.requiredMfa}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className='space-y-1'>
                <Typography.Text className={fieldLabelClass}>
                  Roles <span className='text-red-500'>*</span>
                </Typography.Text>
                <RolesSelect control={control} name='roles' openForm={openForm} />
              </div>
            </div>
          </Card>
        </div>

        <div className='border-t bg-gray-50 px-5 py-3 flex justify-end gap-3'>
          {isDirty && (
            <Button
              type='primary'
              htmlType='submit'
              loading={isSubmitting}
              size='large'
              className='min-w-[100px]'
            >
              Save Changes
            </Button>
          )}
        </div>
      </form>

      {/* Change Password Modal */}
      <Modal
        title='Change Password'
        open={isChangePasswordModalOpen}
        onCancel={handleClosePasswordModal}
        footer={null}
        destroyOnClose={true}
        width={500}
      >
        <form onSubmit={onChangePassword} className='space-y-4'>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Typography.Text className='block font-medium text-gray-700'>
                New Password <span className='text-red-500'>*</span>
              </Typography.Text>
              <CoreInput
                control={passwordControl}
                name='newPassword'
                type='password'
                placeholder='Enter new password'
              />
              <Typography.Text className='text-sm text-gray-500'>
                Password must be at least 8 characters long
              </Typography.Text>
            </div>

            <div className='space-y-2'>
              <Typography.Text className='block font-medium text-gray-700'>
                Confirm New Password <span className='text-red-500'>*</span>
              </Typography.Text>
              <CoreInput
                control={passwordControl}
                name='confirmPassword'
                type='password'
                placeholder='Confirm new password'
              />
              {confirmPassword && newPassword && confirmPassword !== newPassword && (
                <Typography.Text className='text-sm text-red-500'>
                  Passwords do not match
                </Typography.Text>
              )}
            </div>
          </div>

          <div className='flex justify-end gap-3 pt-4 border-t'>
            <Button
              type='default'
              onClick={handleClosePasswordModal}
              disabled={isPasswordSubmitting}
              size='large'
            >
              Cancel
            </Button>
            <Button
              type='primary'
              htmlType='submit'
              loading={isPasswordSubmitting}
              disabled={
                !isPasswordDirty ||
                ((confirmPassword && newPassword && confirmPassword !== newPassword) as boolean)
              }
              size='large'
            >
              Update Password
            </Button>
          </div>
        </form>
      </Modal>

      {/* Change Email Modal */}
      <Modal
        title='Change Email'
        open={isChangeEmailModalOpen}
        onCancel={handleCloseEmailModal}
        footer={null}
        destroyOnClose={true}
        width={500}
      >
        <form onSubmit={onChangeEmail} className='space-y-4'>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Typography.Text className='block font-medium text-gray-700'>
                New Email <span className='text-red-500'>*</span>
              </Typography.Text>
              <CoreInput
                control={emailControl}
                name='newEmail'
                type='email'
                placeholder='Enter new email'
              />
              <Typography.Text className='text-sm text-gray-500'>
                Please enter a valid email address
              </Typography.Text>
            </div>
          </div>

          <div className='flex justify-end gap-3 pt-4 border-t'>
            <Button
              type='default'
              onClick={handleCloseEmailModal}
              disabled={isEmailSubmitting}
              size='large'
            >
              Cancel
            </Button>
            <Button
              type='primary'
              htmlType='submit'
              loading={isEmailSubmitting}
              disabled={!isEmailDirty || !newEmail}
              size='large'
            >
              Update Email
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
